import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Centrifuge } from 'centrifuge';
import { api } from './client';
import {
  appStatusSchema,
  avatarUploadSchema,
  faqListSchema,
  homeSchema,
  leaderboardSchema,
  liveSchema,
  myTournamentSchema,
  profileSchema,
  publicProfileSchema,
  registrationsSchema,
  seasonSchema,
  tournamentResultSchema,
  tournamentSchema,
  type NotificationPrefs,
} from './types';
import { z } from 'zod';

export function useHome() {
  return useQuery({ queryKey: ['home'], queryFn: () => api.get('/home', homeSchema) });
}

export function useFaq() {
  return useQuery({ queryKey: ['faq'], queryFn: () => api.get('/faq', faqListSchema) });
}

/**
 * Public app status (maintenance flag). In normal operation it's fetched once on
 * load + on window focus (no constant polling — the 503 interceptor carries the
 * signal on real traffic). While paused, `poll` turns on a 20s refetch so users
 * recover automatically when maintenance ends. Fail-open (no retry).
 */
export function useAppStatus(poll: boolean) {
  return useQuery({
    queryKey: ['app-status'],
    queryFn: () => api.get('/status', appStatusSchema),
    refetchInterval: poll ? 20_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    retry: false,
  });
}

export function useSeasons() {
  return useQuery({
    queryKey: ['seasons'],
    queryFn: () => api.get('/seasons', z.array(seasonSchema)),
    staleTime: 5 * 60 * 1000,
  });
}

export function useLeaderboard(seasonId: number | null) {
  return useQuery({
    queryKey: ['leaderboard', seasonId],
    queryFn: () =>
      api.get(`/leaderboard${seasonId ? `?seasonId=${seasonId}` : ''}`, leaderboardSchema),
  });
}

export function useProfile(seasonId: number | null) {
  return useQuery({
    queryKey: ['profile', seasonId],
    queryFn: () => api.get(`/me${seasonId ? `?seasonId=${seasonId}` : ''}`, profileSchema),
  });
}

export function useMyTournaments(enabled = true) {
  return useQuery({
    queryKey: ['my-tournaments'],
    queryFn: () => api.get('/me/tournaments', z.array(myTournamentSchema)),
    enabled,
  });
}

export function usePublicProfile(id: number) {
  return useQuery({
    queryKey: ['player', id],
    queryFn: () => api.get(`/players/${id}`, publicProfileSchema),
    enabled: Number.isFinite(id),
  });
}

export function usePlayerTournaments(id: number) {
  return useQuery({
    queryKey: ['player-tournaments', id],
    queryFn: () => api.get(`/players/${id}/tournaments`, z.array(myTournamentSchema)),
    enabled: Number.isFinite(id),
  });
}

export function useTournamentResults(id: number) {
  return useQuery({
    queryKey: ['tournament-results', id],
    queryFn: () => api.get(`/tournaments/${id}/results`, tournamentResultSchema),
    enabled: Number.isFinite(id),
  });
}

const liveTokenSchema = z.object({
  wsUrl: z.string().nullable(),
  token: z.string().nullable(),
  channel: z.string(),
  playerId: z.number(),
});

/**
 * Live tournament state. Primary delivery is Centrifugo WS push (subscribe to
 * `tournament:{id}`); if Centrifugo isn't configured or the socket is down we
 * fall back to 2s polling. The clock still ticks locally from `levelEndsAt`.
 */
export function useTournamentLive(id: number) {
  const qc = useQueryClient();
  const [wsConnected, setWsConnected] = useState(false);

  const query = useQuery({
    queryKey: ['tournament-live', id],
    queryFn: () => api.get(`/tournaments/${id}/live`, liveSchema),
    enabled: Number.isFinite(id),
    // Poll only while the push channel is down (graceful fallback).
    refetchInterval: wsConnected ? false : 2000,
  });

  useEffect(() => {
    if (!Number.isFinite(id)) return;
    let cf: Centrifuge | null = null;
    let cancelled = false;
    (async () => {
      try {
        const info = await api.get(`/tournaments/${id}/live/token`, liveTokenSchema);
        if (cancelled || !info.wsUrl || !info.token) return; // not configured ⇒ keep polling
        cf = new Centrifuge(info.wsUrl, { token: info.token });
        const sub = cf.newSubscription(info.channel);
        sub.on('publication', (ctx) => {
          const parsed = liveSchema.safeParse(ctx.data);
          if (!parsed.success) return;
          const dto = parsed.data;
          // isMe is per-viewer (stripped from the shared push) — stamp it here.
          qc.setQueryData(['tournament-live', id], {
            ...dto,
            players: dto.players.map((p) => ({ ...p, isMe: p.playerId === info.playerId })),
          });
        });
        cf.on('connected', () => setWsConnected(true));
        cf.on('disconnected', () => setWsConnected(false));
        sub.subscribe();
        cf.connect();
      } catch {
        /* token fetch failed / Centrifugo down ⇒ polling fallback stays active */
      }
    })();
    return () => {
      cancelled = true;
      setWsConnected(false);
      cf?.disconnect();
    };
  }, [id, qc]);

  return query;
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { displayName?: string | null; email?: string | null }) =>
      api.patch('/me', profileSchema, patch),
    onSuccess: (data) => {
      qc.setQueryData(['profile', null], data);
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}

export function useUpdateNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: NotificationPrefs) => api.put('/me/notifications', profileSchema, prefs),
    onSuccess: (data) => {
      qc.setQueryData(['profile', null], data);
      void qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload('/me/avatar', avatarUploadSchema, fd);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useTournaments(status: 'upcoming' | 'past') {
  return useQuery({
    queryKey: ['tournaments', status],
    queryFn: () => api.get(`/tournaments?status=${status}`, z.array(tournamentSchema)),
  });
}

export function useTournament(id: number) {
  return useQuery({
    queryKey: ['tournament', id],
    queryFn: () => api.get(`/tournaments/${id}`, tournamentSchema),
    enabled: Number.isFinite(id),
  });
}

export function useRegistrations(id: number) {
  return useQuery({
    queryKey: ['registrations', id],
    queryFn: () => api.get(`/tournaments/${id}/registrations`, registrationsSchema),
    enabled: Number.isFinite(id),
  });
}

export function useRegister(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/tournaments/${id}/register`, tournamentSchema),
    onSuccess: (data) => {
      qc.setQueryData(['tournament', id], data);
      void qc.invalidateQueries({ queryKey: ['tournaments'] });
      void qc.invalidateQueries({ queryKey: ['registrations', id] });
      void qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}

export function useCancelRegistration(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del(`/tournaments/${id}/register`, tournamentSchema),
    onSuccess: (data) => {
      qc.setQueryData(['tournament', id], data);
      void qc.invalidateQueries({ queryKey: ['tournaments'] });
      void qc.invalidateQueries({ queryKey: ['registrations', id] });
      void qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}
